import { Button, Icon } from '@arcadiapower/shrike';
import { useCallback, useEffect, useState } from 'react';
import { FileRejection } from 'react-dropzone';
import { ImageTypeUploader } from '~/components/image-uploader/image-type-uploader.component';
import { DeleteButton } from '~/components/image-uploader/image-uploader.style';
import { Inputs } from '~/components/inputs';
import { Separator } from '~/components/separator';
import { LmiStatus } from '~/machines/review-form';
import { addFileProps, UploadedImage } from '~/utils/files';
import { trackError } from '~/utils/tracking';
import { LMIProofDocTypeSelect } from '../lmi-proof-doc-type-select';
import { handleUpload } from '../utils/handle-uploads';
import { StyledCrate, StyledDetailsContainer, StyledRadioGroup } from './document-details.style';
import { LMIProofDoc } from './lmi-proof-docs-upload.component';

interface DocumentFile {
  file: UploadedImage;
  proofType: string;
}

export interface Document {
  relationship: string;
  name: string;
  documentType: string;
  files: DocumentFile[];
}
export const DocumentDetails = ({ allDocumentTypes, pending, setShouldBeDisabled, setDocs }) => {
  const initialDocument = {
    name: '',
    relationship: '',
    documentType: '',
    files: [],
  };

  const [documents, setDocuments] = useState<Document[]>([initialDocument]);

  const [failure, setFailure] = useState(false);

  const handleDocumentChange = (
    field: keyof typeof initialDocument,
    index: number,
    value: DocumentFile[] | string,
  ) => {
    setDocuments(prevState => {
      const newDocuments = JSON.parse(JSON.stringify(prevState));
      newDocuments[index][field] = value;
      return newDocuments;
    });
  };

  const relationshipOptions = useCallback((index, documentType) => {
    const relationships = {
      [`self-${index}-${documentType}`]: 'Myself',
      [`other-${index}-${documentType}`]: 'Another member of my household',
    };
    return Object.keys(relationships).map(relationship => {
      return {
        children: <p key={relationship}>{relationships[relationship]}</p>,
        value: relationship,
      };
    });
  }, []);

  const addDocFields = () => {
    const newDocs = JSON.parse(JSON.stringify(documents.concat([initialDocument])));
    setDocuments(newDocs);
  };

  const areAllValuesPresent = documents.every(document => {
    return (
      document.documentType && document.name && document.relationship && document.files.length > 0
    );
  });

  const hasErrorDoc = documents.some(document => document.files.some(file => file.file.error));

  useEffect(() => {
    setShouldBeDisabled(areAllValuesPresent && documents.length && !failure && !hasErrorDoc);
    if (areAllValuesPresent && documents.length) {
      setDocs(documents);
    }
  }, [areAllValuesPresent, documents]);

  return (
    <>
      {documents.map((document, index) => {
        return (
          <StyledCrate key={document.documentType}>
            <DeleteButton
              onClick={() => {
                setDocuments(
                  documents
                    .filter((_, i) => i !== index)
                    .map((document, index) => {
                      const newRelationship = document.relationship.replace(
                        /\d+/,
                        index.toString(),
                      );
                      return { ...document, relationship: newRelationship };
                    }),
                );
              }}
              data-testid="delete-upload-button"
            >
              <Icon icon="UIDelete" title="delete-image" />
            </DeleteButton>
            <StyledDetailsContainer>
              <LMIProofDocTypeSelect
                label="Qualifying program"
                allDocumentTypes={allDocumentTypes}
                addedDocumentTypes={documents.map(doc => doc.documentType)}
                selectedDocumentType={document.documentType}
                disabled={document.files.length > 0}
                onChange={newType => handleDocumentChange('documentType', index, newType)}
              />
              {document.documentType && (
                <>
                  <Inputs.NameOnDocument
                    margin="16px 0 0 0 "
                    onChange={value => handleDocumentChange('name', index, value)}
                  />
                  <StyledRadioGroup
                    legend="Relationship"
                    margin="16px 0 0 0"
                    name={`lmi-relationship-${index}-${document.documentType}`}
                    options={relationshipOptions(index, document.documentType)}
                    isChecked={(_, value) => value === document.relationship}
                    onChange={(selection: LmiStatus) => {
                      handleDocumentChange('relationship', index, selection);
                    }}
                    width="initial"
                  />
                  <Separator padding="8px 0" />

                  <ImageTypeUploader
                    key={document.documentType}
                    acceptFileTypes={{ 'image/jpg': ['.jpg', '.jpeg', '.png', '.heic'] }}
                    copy={{ documentType: 'document' }}
                    uploadedImages={document.files}
                    maxImages={0}
                    pageType="multiple"
                    pending={pending}
                    onUploadSuccess={async (proofType: string, file: File, base64Image: string) => {
                      try {
                        setFailure(false);
                        const newDoc: LMIProofDoc = await handleUpload({
                          file,
                          base64Image,
                          documentType: document.documentType,
                        });
                        handleDocumentChange('files', index, [
                          ...document.files,
                          {
                            proofType,
                            file: Object.assign(newDoc.image, { uploadId: Date.now() }),
                          },
                        ]);
                      } catch (error) {
                        trackError(new Error('Error uploading proof document'), {
                          extras: {
                            documentType: document.documentType,
                            error,
                          },
                        });
                        setFailure(true);
                      }
                    }}
                    onUploadFailure={(proofType: string, fileRejection: FileRejection) => {
                      setFailure(true);
                      handleDocumentChange('files', index, [
                        ...document.files,
                        { proofType, file: addFileProps(fileRejection) },
                      ]);
                    }}
                    onDelete={function (id: string): void {
                      setFailure(false);
                      handleDocumentChange('files', index, [
                        ...document.files.filter(file => file.file.id !== id),
                      ]);
                    }}
                  />
                </>
              )}
            </StyledDetailsContainer>
          </StyledCrate>
        );
      })}
      <Button
        disabled={pending || failure || !areAllValuesPresent || hasErrorDoc}
        onClick={addDocFields}
      >
        Add additional program {documents.length >= 1 ? '(optional)' : ''}
      </Button>
    </>
  );
};
